import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, CalendarDays, ChevronDown, ChevronLeft, ChevronRight,
  Clock3, Filter, RefreshCw, Search, ShieldCheck, SlidersHorizontal, UserRound,
} from "lucide-react";
import { api } from "@/lib/api";
import DoxaSelect from "@/components/doxa/DoxaSelect";

const ACTION_LABELS = {
  CREATE_USER: "Usuário criado",
  RESET_ACCESS: "Acesso redefinido",
  TOGGLE_ACTIVE: "Status de acesso alterado",
  REMOVE_USER: "Usuário removido logicamente",
  CREATE_STUDENT: "Aluno criado",
  UPDATE_STUDENT_CELL: "Célula do aluno alterada",
  INACTIVATE_STUDENT: "Aluno inativado",
  REQUEST_CLASS_ENROLLMENT: "Matrícula solicitada",
  APPROVE_CLASS_ENROLLMENT: "Matrícula aprovada",
  APPROVE_REGISTRATION: "Inscrição aprovada",
  REJECT_REGISTRATION: "Inscrição rejeitada",
  JUSTIFY_ABSENCE: "Ausência justificada",
  CORRECT_ATTENDANCE: "Presença corrigida",
  MANUAL_ATTENDANCE: "Presença lançada manualmente",
  GRANT_ONLINE_CHECKIN: "Check-in online liberado",
  REVOKE_ONLINE_CHECKIN: "Check-in online revogado",
  BULK_GRANT_ONLINE_CHECKIN: "Check-in online liberado em massa",
  BULK_REVOKE_ONLINE_CHECKIN: "Check-in online revogado em massa",
  OPEN_LESSON: "Aula aberta",
  CLOSE_LESSON: "Aula encerrada",
  ARCHIVE_LESSON: "Aula arquivada",
  REACTIVATE_LESSON: "Aula reativada",
  CANCEL_LESSON: "Aula cancelada",
  CREATE_EVALUATION: "Avaliação criada",
  PUBLISH_EVALUATION: "Avaliação publicada",
  CLOSE_EVALUATION: "Avaliação encerrada",
  ARCHIVE_EVALUATION: "Avaliação arquivada",
  GRANT_EXTRA_ATTEMPT: "Tentativa extra liberada",
  GRADE_ESSAY: "Resposta dissertativa corrigida",
  PERMISSION_UPDATE: "Permissão alterada",
  PERMISSION_RESTORE_DEFAULT: "Permissão restaurada ao padrão",
  CLEANUP_TEST_DATA: "Limpeza controlada de dados de teste",
  CREATE_CLASS_GROUP: "Turma criada",
  CLASS_GROUP_STATUS: "Status da turma alterado",
  CREATE_REG_LINK: "Link de inscrição criado",
};

const ENTITY_LABELS = {
  user: "Usuário",
  student: "Aluno",
  student_enrollment: "Matrícula",
  class_group: "Turma",
  lesson: "Aula",
  attendance: "Presença",
  online_perm: "Check-in online",
  evaluation: "Avaliação",
  evaluation_answer: "Resposta",
  permission: "Permissão",
  registration_link: "Link de inscrição",
  system: "Sistema",
};

const DETAIL_LABELS = {
  student_id: "Aluno",
  lesson_id: "Aula",
  class_group_id: "Turma",
  user_id: "Usuário",
  evaluation_id: "Avaliação",
  status: "Situação",
  previous_status: "Situação anterior",
  new_status: "Nova situação",
  correction_reason: "Motivo da correção",
  reason: "Motivo",
  target_role: "Perfil afetado",
  permission_label: "Permissão",
  permission_key: "Permissão técnica",
  previous_value: "Antes",
  new_value: "Depois",
  active: "Situação",
};

const DETAIL_ENTITY_TYPES = {
  student_id: "student",
  lesson_id: "lesson",
  class_group_id: "class_group",
  user_id: "user",
  evaluation_id: "evaluation",
};

const STATUS_LABELS = {
  PRESENT: "Presente",
  PRESENT_MANUAL: "Presente — lançamento manual",
  ABSENT: "Ausente",
  JUSTIFIED_ABSENCE: "Ausência justificada",
  SCHEDULED: "Agendada",
  OPEN: "Aberta",
  CLOSED: "Encerrada",
  CANCELLED: "Cancelada",
  ARCHIVED: "Arquivada",
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
};

const ROLE_LABELS = {
  MASTER: "Master",
  ADMIN: "Administrador",
  PROFESSOR: "Professor",
  ALUNO: "Aluno",
};

const CATEGORY_META = {
  users: { label: "Usuários e acesso", dot: "bg-sky-500", soft: "bg-sky-50 text-sky-700" },
  students: { label: "Alunos e inscrições", dot: "bg-emerald-500", soft: "bg-emerald-50 text-emerald-700" },
  attendance: { label: "Presença", dot: "bg-violet-500", soft: "bg-violet-50 text-violet-700" },
  lessons: { label: "Aulas", dot: "bg-amber-500", soft: "bg-amber-50 text-amber-700" },
  evaluations: { label: "Avaliações", dot: "bg-fuchsia-500", soft: "bg-fuchsia-50 text-fuchsia-700" },
  permissions: { label: "Permissões", dot: "bg-rose-500", soft: "bg-rose-50 text-rose-700" },
  system: { label: "Sistema", dot: "bg-slate-700", soft: "bg-slate-100 text-slate-700" },
  other: { label: "Outros", dot: "bg-stone-400", soft: "bg-stone-100 text-stone-700" },
};

const PERIODS = [
  { value: 1, label: "24h" },
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
  { value: 0, label: "Tudo" },
];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function formatDate(value) {
  if (!value) return "Data indisponível";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(date);
}

function relativeDate(value) {
  if (!value) return "";
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  if (!Number.isFinite(diff)) return "";
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days}d`;
  return "";
}

function humanValue(value) {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  if (value == null) return "—";
  if (Array.isArray(value)) return value.map(humanValue).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function humanizeCode(value) {
  return String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function humanDetailValue(event, key, value) {
  const resolved = event.detail_labels?.[key]?.label;
  if (resolved) return resolved;
  if (key === "status" || key === "previous_status" || key === "new_status") {
    return STATUS_LABELS[value] || humanizeCode(value);
  }
  if (key === "target_role") return ROLE_LABELS[value] || humanizeCode(value);
  if (DETAIL_ENTITY_TYPES[key]) {
    const entityName = ENTITY_LABELS[DETAIL_ENTITY_TYPES[key]] || "Registro";
    return `${entityName} não localizado`;
  }
  if (typeof value === "string" && UUID_PATTERN.test(value)) return "Referência técnica registrada";
  return humanValue(value);
}

function detailLabel(key) {
  return DETAIL_LABELS[key] || humanizeCode(key);
}

function MetricCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="doxa-card p-4 sm:p-5 min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-[#6B655E]">{label}</p>
          <p className="font-display text-2xl sm:text-3xl font-bold mt-1 text-[#1F1F1F]">{value ?? "—"}</p>
          <p className="text-xs text-[#8A8378] mt-1">{hint}</p>
        </div>
        <div className="w-10 h-10 rounded-xl bg-[#F6EFE4] text-[#F75A1B] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function DetailCard({ label, value, hint, accent = false }) {
  return (
    <div className={`rounded-xl border px-3.5 py-3 min-w-0 ${accent ? "border-[#F6C7B3] bg-[#FFF8F4]" : "border-[#E8E0D5] bg-white"}`}>
      <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#8A8378]">{label}</p>
      <p className="mt-1 text-sm font-semibold text-[#282522] break-words">{value || "—"}</p>
      {hint && <p className="mt-1 text-xs text-[#8A8378] break-words">{hint}</p>}
    </div>
  );
}

function TechnicalField({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#9A9287]">{label}</p>
      <code className="mt-1 block text-[11px] leading-5 text-[#4B4640] break-all">{value || "—"}</code>
    </div>
  );
}

function EventDetails({ event }) {
  const rawDetails = Object.entries(event.details || {});
  const humanDetails = rawDetails.filter(([key]) => !(key === "permission_key" && event.details?.permission_label));
  const title = ACTION_LABELS[event.action] || humanizeCode(event.action) || "Evento registrado";
  const entityName = ENTITY_LABELS[event.entity_type] || "Registro";
  const target = event.entity_label || `${entityName} registrado`;
  const source = event.source === "permissions" ? "Auditoria de permissões" : "Auditoria operacional";

  return (
    <div className="mt-4 rounded-2xl border border-[#EAE1D5] bg-[#FBF8F3] p-3.5 sm:p-4" data-testid={`audit-details-${event.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#8A8378]">Resumo do evento</p>
          <p className="text-sm text-[#6B655E] mt-1">Informações em linguagem operacional. Os identificadores internos continuam disponíveis abaixo.</p>
        </div>
        <span className="text-[11px] font-medium rounded-full bg-white border border-[#E8E0D5] px-2.5 py-1 text-[#6B655E]">{source}</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3">
        <DetailCard label="O que aconteceu" value={title} accent />
        <DetailCard label={entityName} value={target} />
        <DetailCard
          label="Responsável"
          value={event.actor_name || "Usuário indisponível"}
          hint={[ROLE_LABELS[event.actor_role] || event.actor_role, event.actor_email].filter(Boolean).join(" · ")}
        />
      </div>

      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#8A8378] mb-2">Contexto</p>
        {humanDetails.length === 0 ? (
          <div className="rounded-xl bg-white border border-[#E8E0D5] px-3.5 py-3 text-sm text-[#8A8378]">Este evento não possui informações adicionais.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2.5">
            {humanDetails.map(([key, value]) => (
              <DetailCard key={key} label={detailLabel(key)} value={humanDetailValue(event, key, value)} />
            ))}
          </div>
        )}
      </div>

      <details className="group mt-3 rounded-xl border border-[#E5DDD2] bg-white/80">
        <summary className="list-none cursor-pointer flex items-center justify-between gap-3 px-3.5 py-3 text-xs font-semibold text-[#6B655E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 rounded-xl">
          <span>Detalhes técnicos</span>
          <ChevronDown className="w-4 h-4 text-[#8A8378] transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-3.5 pb-3.5 pt-1 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          <TechnicalField label="Ação" value={event.action} />
          <TechnicalField label="Tipo de alvo" value={event.entity_type} />
          <TechnicalField label="ID do alvo" value={event.entity_id} />
          <TechnicalField label="ID do evento" value={event.id} />
          <TechnicalField label="Origem" value={event.source} />
          {event.actor_email && <TechnicalField label="Conta do responsável" value={event.actor_email} />}
          {rawDetails.map(([key, value]) => (
            <TechnicalField key={`technical-${key}`} label={key} value={humanValue(value)} />
          ))}
        </div>
      </details>
    </div>
  );
}

function EventRow({ event, expanded, onToggle }) {
  const cat = CATEGORY_META[event.category] || CATEGORY_META.other;
  const title = ACTION_LABELS[event.action] || event.action?.replaceAll("_", " ") || "Evento registrado";
  const entityName = ENTITY_LABELS[event.entity_type] || "Registro";
  const target = event.entity_label || `${entityName} registrado`;
  return (
    <article className="doxa-card p-4 sm:p-5" data-testid={`audit-event-${event.id}`}>
      <button type="button" onClick={onToggle} className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 rounded-xl" aria-expanded={expanded}>
        <div className="flex items-start gap-3 sm:gap-4">
          <div className={`mt-0.5 w-10 h-10 rounded-xl ${cat.soft} flex items-center justify-center shrink-0`}>
            {event.severity === "high" ? <AlertTriangle className="w-5 h-5" /> : <Activity className="w-5 h-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-[#1F1F1F]">{title}</h3>
                  <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${cat.soft}`}>{cat.label}</span>
                  {event.severity === "high" && <span className="text-[11px] px-2 py-1 rounded-full font-medium bg-red-50 text-red-700">Atenção</span>}
                  {event.severity === "medium" && <span className="text-[11px] px-2 py-1 rounded-full font-medium bg-amber-50 text-amber-700">Relevante</span>}
                </div>
                <p className="text-sm text-[#6B655E] mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-[#3F3A34]">{event.actor_name || "Usuário indisponível"}</span>
                  {event.actor_role && <span className="text-xs rounded-full bg-[#F1ECE4] px-2 py-0.5">{ROLE_LABELS[event.actor_role] || event.actor_role}</span>}
                  <span className="text-[#B0A79B]">→</span>
                  <span className="truncate max-w-full">{target}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 text-right">
                <div>
                  <p className="text-xs font-medium text-[#6B655E]">{relativeDate(event.created_at)}</p>
                  <p className="text-[11px] text-[#9A9287] mt-0.5">{formatDate(event.created_at)}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#8A8378] transition-transform ${expanded ? "rotate-180" : ""}`} />
              </div>
            </div>
          </div>
        </div>
      </button>
      {expanded && <EventDetails event={event} />}
    </article>
  );
}

export default function Audit() {
  const [overview, setOverview] = useState(null);
  const [meta, setMeta] = useState({ actors: [], actions: [], entity_types: [], categories: [] });
  const [result, setResult] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({ days: 30, category: "", actor_user_id: "", source: "", entity_type: "", action: "", q: "" });
  const [page, setPage] = useState(1);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const { data } = await api.get("/audit/overview");
      setOverview(data);
    } catch {
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/audit/metadata");
      setMeta(data);
    } catch {
      setMeta({ actors: [], actions: [], entity_types: [], categories: [] });
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, page_size: 30, ...filters };
      Object.keys(params).forEach((key) => { if (params[key] === "") delete params[key]; });
      const { data } = await api.get("/audit/events", { params });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível carregar a auditoria.");
      setResult({ items: [], page: 1, pages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { loadOverview(); loadMeta(); }, [loadOverview, loadMeta]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setFilters((current) => ({ ...current, q: searchInput.trim() }));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const changeFilter = (key, value) => {
    setPage(1);
    setExpandedId(null);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    setSearchInput("");
    setPage(1);
    setExpandedId(null);
    setFilters({ days: 30, category: "", actor_user_id: "", source: "", entity_type: "", action: "", q: "" });
  };

  const refreshAll = () => { setExpandedId(null); loadOverview(); loadMeta(); loadEvents(); };
  const hasAdvanced = filters.actor_user_id || filters.source || filters.entity_type || filters.action;
  const maxCategory = useMemo(() => Math.max(1, ...(overview?.categories_7d || []).map((item) => item.count)), [overview]);

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto" data-testid="audit-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B655E] font-medium">Rastreabilidade operacional</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold inline-flex items-center gap-2 mt-1"><ShieldCheck className="w-7 h-7 text-[#F75A1B]" /> Auditoria</h1>
          <p className="text-sm text-[#6B655E] mt-2 max-w-2xl">Veja quem fez o quê, quando e sobre qual registro. A tela reúne operações e mudanças de permissão sem expor segredos.</p>
        </div>
        <button type="button" onClick={refreshAll} className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-[#DED6CA] bg-white text-sm font-medium hover:bg-[#F8F3EA] transition-colors" data-testid="audit-refresh">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5" aria-label="Resumo de auditoria">
        <MetricCard icon={Clock3} label="Últimas 24h" value={overviewLoading ? "…" : overview?.total_24h ?? 0} hint="eventos registrados" />
        <MetricCard icon={CalendarDays} label="Últimos 7 dias" value={overviewLoading ? "…" : overview?.total_7d ?? 0} hint="atividade recente" />
        <MetricCard icon={UserRound} label="Atores ativos" value={overviewLoading ? "…" : overview?.active_actors_7d ?? 0} hint="nos últimos 7 dias" />
        <MetricCard icon={Activity} label="Últimos 30 dias" value={overviewLoading ? "…" : overview?.total_30d ?? 0} hint="eventos no período" />
      </section>

      <section className="doxa-card p-4 sm:p-5 mb-5" data-testid="audit-category-overview">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="font-semibold">Atividade por área</h2>
            <p className="text-xs text-[#8A8378] mt-0.5">Distribuição dos últimos 7 dias.</p>
          </div>
          <span className="text-xs text-[#8A8378]">Clique em uma área para filtrar</span>
        </div>
        {overviewLoading ? (
          <div className="h-20 rounded-xl bg-[#F4EEE5] animate-pulse" />
        ) : (overview?.categories_7d || []).length === 0 ? (
          <p className="text-sm text-[#8A8378] py-3">Ainda não há eventos recentes para distribuir.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {overview.categories_7d.map((item) => {
              const cat = CATEGORY_META[item.key] || CATEGORY_META.other;
              const selected = filters.category === item.key;
              return (
                <button key={item.key} type="button" onClick={() => changeFilter("category", selected ? "" : item.key)} className={`text-left rounded-xl border px-3 py-3 transition-colors ${selected ? "border-[#F75A1B] bg-[#FFF7F3]" : "border-[#E8E0D5] hover:bg-[#FAF7F2]"}`}>
                  <div className="flex items-center justify-between gap-2 text-sm"><span className="inline-flex items-center gap-2 font-medium"><span className={`w-2 h-2 rounded-full ${cat.dot}`} />{item.label}</span><strong>{item.count}</strong></div>
                  <div className="h-1.5 rounded-full bg-[#EEE8DF] overflow-hidden mt-2.5"><div className={`h-full rounded-full ${cat.dot}`} style={{ width: `${Math.max(6, (item.count / maxCategory) * 100)}%` }} /></div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="doxa-card p-3 sm:p-4 mb-5" data-testid="audit-filters">
        <div className="flex items-center gap-2 mb-3"><Filter className="w-4 h-4 text-[#F75A1B]" /><h2 className="text-sm font-semibold">Encontrar um evento</h2></div>
        <div className="flex gap-2 overflow-x-auto pb-1 mb-3 scrollbar-hide" aria-label="Período">
          {PERIODS.map((period) => (
            <button key={period.value} type="button" onClick={() => changeFilter("days", period.value)} className={`shrink-0 px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${filters.days === period.value ? "bg-[#1F1F1F] text-white border-[#1F1F1F]" : "bg-white border-[#E0D9CD] text-[#6B655E] hover:bg-[#F8F3EA]"}`}>{period.label}</button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1.5fr_1fr_1fr_1fr] gap-2.5">
          <label className="relative min-w-0">
            <span className="sr-only">Buscar na auditoria</span>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9287]" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Buscar ação, pessoa ou ID…" className="w-full min-h-[44px] pl-10 pr-3 py-2.5 rounded-xl border border-[#E0D9CD] bg-white text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#F75A1B]/25" />
          </label>
          <DoxaSelect
            value={filters.actor_user_id}
            onValueChange={(value) => changeFilter("actor_user_id", value)}
            options={[
              { value: "", label: "Todas as pessoas" },
              ...meta.actors.map((actor) => ({ value: actor.id, label: `${actor.name} · ${actor.role}` })),
            ]}
            ariaLabel="Filtrar por pessoa"
            className="h-11 w-full min-w-0 text-base sm:text-sm"
          />
          <DoxaSelect
            value={filters.entity_type}
            onValueChange={(value) => changeFilter("entity_type", value)}
            options={[
              { value: "", label: "Todos os alvos" },
              ...meta.entity_types.map((type) => ({ value: type, label: ENTITY_LABELS[type] || type })),
            ]}
            ariaLabel="Filtrar por tipo de alvo"
            className="h-11 w-full min-w-0 text-base sm:text-sm"
          />
          <DoxaSelect
            value={filters.source}
            onValueChange={(value) => changeFilter("source", value)}
            options={[
              { value: "", label: "Todas as origens" },
              { value: "operations", label: "Operações" },
              { value: "permissions", label: "Permissões" },
            ]}
            ariaLabel="Filtrar por origem"
            className="h-11 w-full min-w-0 text-base sm:text-sm"
          />
        </div>

        <details className="mt-3 group" open={Boolean(hasAdvanced && filters.action)}>
          <summary className="list-none cursor-pointer inline-flex items-center gap-2 text-xs font-medium text-[#6B655E] py-2"><SlidersHorizontal className="w-3.5 h-3.5" /> Filtro técnico por ação <ChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" /></summary>
          <div className="grid sm:grid-cols-[1fr_auto] gap-2 pt-1">
            <DoxaSelect
              value={filters.action}
              onValueChange={(value) => changeFilter("action", value)}
              options={[
                { value: "", label: "Todas as ações técnicas" },
                ...meta.actions.map((action) => ({ value: action, label: ACTION_LABELS[action] || action })),
              ]}
              ariaLabel="Filtrar por ação técnica"
              className="h-11 w-full min-w-0 text-base sm:text-sm"
            />
            <button type="button" onClick={clearFilters} className="min-h-[44px] px-3 rounded-xl text-sm text-[#6B655E] hover:bg-[#F6EFE4]">Limpar filtros</button>
          </div>
        </details>
      </section>

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold">Linha do tempo</h2>
          <p className="text-xs text-[#8A8378] mt-0.5">{loading ? "Carregando…" : `${result.total} evento${result.total === 1 ? "" : "s"} encontrado${result.total === 1 ? "" : "s"}`}</p>
        </div>
        {(filters.category || filters.actor_user_id || filters.source || filters.entity_type || filters.action || filters.q || filters.days !== 30) && (
          <button type="button" onClick={clearFilters} className="text-xs font-medium text-[#F75A1B] hover:underline">Voltar à visão padrão</button>
        )}
      </div>

      {error ? (
        <div className="doxa-card p-5 border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>
      ) : loading ? (
        <div className="space-y-3">{[1, 2, 3, 4].map((item) => <div key={item} className="h-24 rounded-2xl bg-[#F0EAE1] animate-pulse" />)}</div>
      ) : result.items.length === 0 ? (
        <div className="doxa-card p-8 text-center">
          <Search className="w-8 h-8 mx-auto text-[#B0A79B]" />
          <h3 className="font-semibold mt-3">Nenhum evento encontrado</h3>
          <p className="text-sm text-[#8A8378] mt-1">Ajuste os filtros ou amplie o período consultado.</p>
          <button type="button" onClick={clearFilters} className="mt-4 px-4 py-2 rounded-xl bg-[#F6EFE4] text-sm font-medium">Limpar filtros</button>
        </div>
      ) : (
        <div className="space-y-3">
          {result.items.map((event) => <EventRow key={`${event.source}-${event.id}`} event={event} expanded={expandedId === event.id} onToggle={() => setExpandedId((current) => current === event.id ? null : event.id)} />)}
        </div>
      )}

      {!loading && result.total > 0 && (
        <div className="mt-5 flex items-center justify-between gap-3 doxa-card px-3 py-3 sm:px-4">
          <button type="button" disabled={page <= 1} onClick={() => { setExpandedId(null); setPage((value) => Math.max(1, value - 1)); }} className="inline-flex items-center gap-1.5 min-h-[42px] px-3 rounded-xl text-sm font-medium disabled:opacity-35 hover:bg-[#F6EFE4]"><ChevronLeft className="w-4 h-4" /> Anterior</button>
          <span className="text-xs sm:text-sm text-[#6B655E]">Página <strong className="text-[#1F1F1F]">{result.page}</strong> de <strong className="text-[#1F1F1F]">{result.pages}</strong></span>
          <button type="button" disabled={page >= result.pages} onClick={() => { setExpandedId(null); setPage((value) => Math.min(result.pages, value + 1)); }} className="inline-flex items-center gap-1.5 min-h-[42px] px-3 rounded-xl text-sm font-medium disabled:opacity-35 hover:bg-[#F6EFE4]">Próxima <ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}
